
// New code for bug fix

import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import drilldown from "highcharts/modules/drilldown";
import Select from "react-select";
import redirectURL from "../redirectURL";

drilldown(Highcharts);

const SKUSalesDrilldownChart = ({ chartData, startDate, endDate }) => {
  const [rawData, setRawData] = useState(Array.isArray(chartData) ? chartData : []);
  const [allSkuCodes, setAllSkuCodes] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [fromDate, setFromDate] = useState(startDate);
  const [toDate, setToDate] = useState(endDate);
  const [loading, setLoading] = useState(false);

  // Sync when parent gives fresh chartData (initial full payload)
  useEffect(() => {
    const arr = Array.isArray(chartData) ? chartData : [];
    setRawData(arr);
    const initialSkus = Array.from(new Set(arr.map(d => d.sku_code)));
    setAllSkuCodes(initialSkus);
  }, [chartData]);

  // Keep local date state in sync with props if they change
  useEffect(() => {
    setFromDate(startDate);
  }, [startDate]);

  useEffect(() => {
    setToDate(endDate);
  }, [endDate]);

  // Dropdown options are built from a stable list (allSkuCodes), not from filtered rawData
  const skuOptions = useMemo(
    () => allSkuCodes.map(s => ({ value: s, label: s })),
    [allSkuCodes]
  );

  const resetData = async () => {
      setLoading(true);
      try {
        const params = {
          from_date: fromDate,
          to_date: toDate
        };
        const res = await redirectURL.post("inventory/getDailySkuSalesChartData", params);
        if (res.status === 200 && res.data && res.data.status === "success") {
          const newData = JSON.parse(res.data.data.drilldown_df) || [];
          setRawData(newData);

          // Optional: merge new SKUs so the dropdown can grow if backend returns unseen SKUs
          const merged = new Set(allSkuCodes);
          for (let i = 0; i < newData.length; i++) merged.add(newData[i].sku_code);
          setAllSkuCodes(Array.from(merged));
        }
      } catch (e) {
        // swallow or toast
      } finally {
        setLoading(false);
      }
    };

  // Fetch filtered data for the chart, but do NOT shrink the options
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          from_date: fromDate,
          to_date: toDate,
          skus: JSON.stringify(selectedSkus),
        };
        const res = await redirectURL.post("inventory/getDailySkuSalesChartData", params);
        if (res.status === 200 && res.data && res.data.status === "success") {
          const newData = JSON.parse(res.data.data.drilldown_df) || [];
          setRawData(newData);

          // Optional: merge new SKUs so the dropdown can grow if backend returns unseen SKUs
          const merged = new Set(allSkuCodes);
          for (let i = 0; i < newData.length; i++) merged.add(newData[i].sku_code);
          setAllSkuCodes(Array.from(merged));
        }
      } catch (e) {
        // swallow or toast
      } finally {
        setLoading(false);
      }
    };
    // Only call when user changes filters/dates/selection
    if (fromDate && toDate) fetchData();
  }, [fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log("this fuction has been triggered now!")
    const fetchData = async () => {
      const selectedSku = selectedSkus.length > 0 
      if (!selectedSku && chartData.length !== 20 ) {
          await resetData()
      }
    }

    fetchData()
  },[selectedSkus])
  // Filter chart by selected SKUs (if API returns all; harmless if it’s already filtered)
  const filteredData =
    selectedSkus.length === 0
      ? rawData
      : rawData.filter(d => selectedSkus.indexOf(d.sku_code) !== -1);

  // Aggregate for chart
  const skuMap = {};
  for (let i = 0; i < filteredData.length; i++) {
    const sku = filteredData[i].sku_code;
    const store = filteredData[i].destination_store_name;
    const qty = Number(filteredData[i].sale_quantity) || 0;
    if (!skuMap[sku]) skuMap[sku] = { total: 0, stores: {} };
    skuMap[sku].total += qty;
    skuMap[sku].stores[store] = (skuMap[sku].stores[store] || 0) + qty;
  }

  const sortedSKUs = Object.keys(skuMap)
    .map(k => [k, skuMap[k]])
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 20);

  const mainSeries = sortedSKUs.map(([sku, data]) => ({
    name: sku,
    y: data.total,
    drilldown: sku,
  }));

  const drilldownSeries = sortedSKUs.map(([sku, data]) => ({
    id: sku,
    name: "Sales in Destinations for " + sku,
    data: Object.keys(data.stores).map(store => [store, data.stores[store]]),
  }));

  const chartOptions = {
    chart: { type: "column", height: "500px" },
    title: { text: "Sales Summary (SKU Wise)" },
    xAxis: { type: "category" },
    yAxis: {
      title: { text: "Sale Quantity" },
      labels: {
        formatter: function () {
          return this.value / 1000 + "k";
        },
      },
    },
    credits: { enabled: false },
    tooltip: {
      headerFormat: "<b>{series.name}</b><br>",
      pointFormat: "{point.name}: <b>{point.y}</b>",
    },
    plotOptions: {
      column: { dataLabels: { enabled: true, format: "{point.y}" } },
    },
    series: [
      {
        name: "Sku Code",
        colorByPoint: true,
        data: mainSeries,
      },
    ],
    drilldown: { series: drilldownSeries },
  };

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 24,
          marginBottom: 24,
        }}
      >
        <div style={{ width: "300px" }}>
          <label style={{ fontWeight: 600, fontSize: 16 }}>Select SKU`s</label>
          <Select
            isMulti
            options={skuOptions}
            value={skuOptions.filter(o => selectedSkus.indexOf(o.value) !== -1)}
            onChange={arr => setSelectedSkus((arr || []).map(s => s.value))}
            // isDisabled={loading}
            placeholder="Filter by SKU"
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <label style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>From Date:</label>
          <input
            id="from-date-skuchart"
            type="date"
            value={fromDate}
            min={startDate}
            max={toDate}
            onChange={e => setFromDate(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: 14,
              background: "#fff",
              marginBottom: 0,
              minWidth: 140,
            }}
            onClick={function () {
              const el = document.getElementById("from-date-skuchart");
              if (el && el.showPicker) el.showPicker();
            }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <label style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>To Date:</label>
          <input
            id="to-date-skuchart"
            type="date"
            value={toDate}
            min={fromDate}
            max={endDate}
            onChange={e => setToDate(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: 14,
              background: "#fff",
              marginBottom: 0,
              minWidth: 140,
            }}
            onClick={function () {
              const el = document.getElementById("to-date-skuchart");
              if (el && el.showPicker) el.showPicker();
            }}
          />
        </div>
      </div>

      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  );
};

export default SKUSalesDrilldownChart;


// Previous code 

// import React, { useEffect, useState } from 'react';
// import Highcharts from 'highcharts';
// import HighchartsReact from 'highcharts-react-official';
// import drilldown from 'highcharts/modules/drilldown';
// import Select from 'react-select';
// import redirectURL from "../redirectURL"

// drilldown(Highcharts);

// const SKUSalesDrilldownChart = ({chartData,startDate, endDate}) => {
//   const [rawData ,setRawData] = useState(chartData)
//   // Instead of this:

//   // Do this:
//   // const rawData = skuwiseStoresChartData;
//   const allSKUs = [...new Set(rawData.map((d) => d.sku_code))];


//   const [selectedSkus, setSelectedSkus] = useState([]);

//   // Add date state (but do not filter chart by date)
//   const allDates = [
//     ...new Set(
//       rawData
//         .map((d) => d.invoice_date && d.invoice_date.slice(0, 10))
//         .filter(Boolean)
//     ),
//   ].sort();
//   const [fromDate, setFromDate] = useState(startDate);
//   const [toDate, setToDate] = useState(endDate);
//   const [loading, setLoading] = useState(false);
//   const [dates, setDates] = useState([]);

//   const skuOptions = allSKUs.map((sku) => ({
//     value: sku,
//     label: sku,
//   }));

//   const filteredData = rawData.filter((d) => {
//     return selectedSkus.length === 0 || selectedSkus.includes(d.sku_code);
//   });

//   const skuMap = {};

//   filteredData.forEach(({ sku_code, destination_store_name, sale_quantity }) => {
//     if (!skuMap[sku_code]) {
//       skuMap[sku_code] = { total: 0, stores: {} };
//     }
//     skuMap[sku_code].total += sale_quantity;
//     skuMap[sku_code].stores[destination_store_name] = sale_quantity;
//   });

//   // Convert SKU Map to array and sort by total sales descending
// const sortedSKUs = Object.entries(skuMap)
//   .sort((a, b) => b[1].total - a[1].total)
//   .slice(0, 20); // Take top 20

// const mainSeries = sortedSKUs.map(([sku, data]) => ({
//   name: sku,
//   y: data.total,
//   drilldown: sku,
// }));

// const drilldownSeries = sortedSKUs.map(([sku, data]) => ({
//   id: sku,
//   name: `Sales in Destinations for ${sku}`,
//   data: Object.entries(data.stores),
// }));

//   const chartOptions = {
//     chart: {
//       type: 'column',
//       height : "500px"
//     },
//     title: {
//       text: 'Sales Summary (SKU Wise)',
//     },
//     xAxis: {
//       type: 'category',
//       // title: { text: 'SKU Code' },
//     },
//     yAxis: {
//       title: { text: 'Sale Quantity' },
//       labels: {
//         formatter() {
//           return this.value / 1000 + 'k';
//         },
//       },
//     },
//     credits: { enabled: false },
//     tooltip: {
//       headerFormat: '<b>{series.name}</b><br>',
//       pointFormat: '{point.name}: <b>{point.y}</b>',
//     },
//     plotOptions: {
//       column: {
//         dataLabels: {
//           enabled: true,
//           format: '{point.y}',
//         },
//       },
//     },
//     series: [
//       {
//         name: 'Sku Code',
//         colorByPoint: true,
//         data: mainSeries,
//       },
//     ],
//     drilldown: {
//       series: drilldownSeries,
//     },
//   };

//   // API call on Apply (does not update chart)
//   const handleApply = async () => {
//     setLoading(true);
//     try {
//       var params = {
//         from_date: fromDate,
//         to_date: toDate,
//         skus: JSON.stringify(selectedSkus),
//       }
//       // console.log(params, "params");

//       const dailySalesResponse = await redirectURL.post("inventory/getDailySkuSalesChartData", params)
//       if (dailySalesResponse.status === 200) {
//         if (dailySalesResponse.data.status === "success") {
//           setRawData(JSON.parse(dailySalesResponse.data.data.drilldown_df) || []);
//         } 
//       }
//       // Do not update chart, just call API
//     } catch (err) {
//       alert('Failed to call API');
//     } finally {
//       setLoading(false);
//     }
//   };
//   // Set all dates & default selections
// useEffect(() => {
//   const fetchData = async () => {
//     setLoading(true);
//     try {
//       const params = {
//         from_date: fromDate,
//         to_date: toDate,
//         skus: JSON.stringify(selectedSkus),
//       };

//       const dailySalesResponse = await redirectURL.post("inventory/getDailySkuSalesChartData", params);
      
//       if (dailySalesResponse.status === 200) {
//         if (dailySalesResponse.data.status === "success") {
//           setRawData(JSON.parse(dailySalesResponse.data.data.drilldown_df) || []);
//         }
//       }
//     } catch (err) {
//       alert('Failed to call API');
//     } finally {
//       setLoading(false);
//     }
//   };

//   fetchData();
// }, [fromDate, toDate, selectedSkus]);

// console.log(skuOptions, "skuOptions");



//   return (
//     <div style={{ padding: '20px' }}>
//       <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 24, marginBottom: 24 }}>
//         <div style={{ width: '300px' }}>
//           <label style={{fontWeight: 600, fontSize: 16 }}>Select SKU`s</label>
//           <Select
//             isMulti
//             options={skuOptions}
//             value={selectedSkus.map((sku) => ({ value: sku, label: sku }))}
//             onChange={(skus) => setSelectedSkus(skus.map((sku) => sku.value))}
//             placeholder="Filter by SKU"
//           />
//         </div>
//         <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
//           <label style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>From Date:</label>
//           <input
//             id="from-date-skuchart"
//             type="date"
//             value={fromDate}
//             min={startDate}
//             max={toDate}
//             onChange={e => setFromDate(e.target.value)}
//             style={{
//               padding: '6px 12px',
//               borderRadius: 4,
//               border: '1px solid #ccc',
//               fontSize: 14,
//               background: '#fff',
//               marginBottom: 0,
//               minWidth: 140
//             }}
//             onClick={() => {
//               const dateInput = document.getElementById('from-date-skuchart');
//               if (dateInput && dateInput.showPicker) {
//                 dateInput.showPicker();
//               }
//             }}
//             // disabled={allDates.length === 0}
//           />
//         </div>
//         <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
//           <label  style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>To Date:</label>
//           <input
//             id="to-date-skuchart"
//             type="date"
//             value={toDate}
//             min={fromDate}
//             max={endDate}
//             onChange={e => setToDate(e.target.value)}
//             style={{
//               padding: '6px 12px',
//               borderRadius: 4,
//               border: '1px solid #ccc',
//               fontSize: 14,
//               background: '#fff',
//               marginBottom: 0,
//               minWidth: 140
//             }}
//             onClick={() => {
//               const dateInput = document.getElementById('to-date-skuchart');
//               if (dateInput && dateInput.showPicker) {
//                 dateInput.showPicker();
//               }
//             }}
//             // disabled={allDates.length === 0} 
//           />
//         </div>
//         {/* <button
//           onClick={handleApply}
//           // disabled={loading || allDates.length === 0}
//           style={{
//             padding: '8px 20px',
//             borderRadius: 4,
//             border: 'none',
//             background: '#1976d2',
//             color: '#fff',
//             fontWeight: 500,
//             fontSize: 13,
//             cursor: loading ? 'not-allowed' : 'pointer',
//             marginTop: 28
//           }}
//         >
//           {loading ? 'Applying...' : 'Apply'}
//         </button> */}
//       </div>
//       <HighchartsReact highcharts={Highcharts} options={chartOptions} />
//     </div>
//   );
// };

// export default SKUSalesDrilldownChart;